type CnaiDataAttrs<T> = { [K in keyof T & string as `data-${K}`]: T[K] };

// every cnai-* attribute also has an HTML-valid data-cnai-* spelling that CNAI treats identically —
// this rewrites keys to that form when asDataAttr is set, so callers never hand-build either spelling.
// asDataAttr is a runtime value at every call site, so the return type is a union of both spellings
// rather than overloads — spread the result, don't read individual keys off it.
function withCnaiAttrPrefix<T extends Record<string, string>>(
  attrs: T,
  asDataAttr: boolean | undefined,
): CnaiDataAttrs<T> | T {
  if (!asDataAttr) return attrs;

  return Object.fromEntries(
    Object.entries(attrs).map(([key, value]) => [`data-${key}`, value]),
  ) as CnaiDataAttrs<T>;
}

export type CnaiFieldFormat = "bullet" | "numbered" | "paragraph";

export type CnaiFieldType =
  | "boolean"
  | "date"
  | "end_time"
  | "multiple_choice"
  | "number"
  | "single_choice"
  | "start_end_time"
  | "start_time"
  | "string"
  | "table"
  | "template";

// a field is conditional on exactly one operator against another field's value — see the CNAI
// tagging doc's "Conditional fields" section for precedence rules if more than one is ever set
type CnaiFieldCondition =
  | { conditionId: string; contains: string }
  | { conditionId: string; containsAny: string[] }
  | { conditionId: string; equals: string };

type CnaiFieldProps = {
  // emit the data-cnai-* spelling instead of cnai-* — functionally identical to CNAI. No lint rule
  // in this repo currently requires it (react/no-unknown-property is not enabled); it exists as an
  // escape hatch if a bare cnai-* attribute ever trips a "no unknown DOM property" rule or a
  // stricter JSX prop type on the target element
  asDataAttr?: boolean;
  condition?: CnaiFieldCondition;
  description?: string;
  // when false, no cnai-* attributes are emitted (returns an empty object) — lets a shared field
  // component render untagged on surfaces where CNAI should be off (e.g. live-call widgets) while
  // staying tagged on the post-call note. Defaults to true.
  enable?: boolean;
  format?: CnaiFieldFormat;
  id: string;
  instructions?: string;
  name: string;
  option?: string;
  // required for "template"-type fields; ignored otherwise
  template?: string;
  type: CnaiFieldType;
};

// builds the cnai-template-field-* attributes clinically.ai reads off form fields; spread the result onto the target element
export function makeCnaiProps({
  asDataAttr,
  condition,
  description,
  enable = true,
  format,
  id,
  instructions,
  name,
  option,
  template,
  type,
}: CnaiFieldProps) {
  if (!enable) return {};

  return withCnaiAttrPrefix(
    {
      "cnai-template-field-id": id,
      "cnai-template-field-name": name,
      "cnai-template-field-type": type,
      ...(option !== undefined && { "cnai-template-field-option": option }),
      ...(format !== undefined && { "cnai-template-field-format": format }),
      ...(instructions !== undefined && { "cnai-template-field-instructions": instructions }),
      ...(description !== undefined && { "cnai-template-field-description": description }),
      ...(template !== undefined && { "cnai-template-field-template": template }),
      ...(condition !== undefined && {
        "cnai-field-condition-id": condition.conditionId,
        ...("equals" in condition && { "cnai-field-condition-equals": condition.equals }),
        ...("contains" in condition && { "cnai-field-condition-contains": condition.contains }),
        ...("containsAny" in condition && {
          "cnai-field-condition-contains-any": JSON.stringify(condition.containsAny),
        }),
      }),
    },
    asDataAttr,
  );
}

// the doc's recognized document types; anything else falls back to "Other" on the CNAI side, but
// keeping this as a union catches typos here rather than silently degrading in the extension
export type CnaiTemplateType =
  | "Aftercare Plan"
  | "Assessment"
  | "Biopsychosocial Assessment"
  | "Care Plan"
  | "Case Note"
  | "Diagnostic Test Results"
  | "Discharge Summary"
  | "Family / Collateral Contacts"
  | "Follow-up / Recall Systems"
  | "Group Dynamics Analysis"
  | "Group Progress Note"
  | "Group Therapy Note"
  | "Handover Note"
  | "Incident Report"
  | "Individual Progress Note"
  | "Infection Control"
  | "Initial Psychiatric Evaluation"
  | "Insurance / Billing"
  | "Intake"
  | "Mandated Reporting Form"
  | "Medical History / Physical Exam"
  | "Medication Administration Record (MAR)"
  | "Mental Status Exam"
  | "Nursing Assessment"
  | "Other"
  | "Patient / Family Feedback"
  | "Patient Monitoring"
  | "Peer Consultation Note"
  | "Physical Therapy Reevaluation"
  | "Preventive Care Plan"
  | "Prescription Records"
  | "Problem List"
  | "Procedure Note"
  | "Progress Note"
  | "Referral / Consultation"
  | "Relapse Prevention Plan"
  | "Release of Information"
  | "Safety Plan"
  | "Screening"
  | "Supervision Note"
  | "Surgical Note"
  | "Treatment Plan"
  | "Treatment Plan Update"
  | "Vital Signs Record"
  | "Weekly / Monthly Summary"
  | "Wound Care Intake"
  | "Wound Care Progress Note";

type CnaiTemplateProps = {
  // see makeCnaiProps — same data-cnai-* escape hatch
  asDataAttr?: boolean;
  // when false, no cnai-* attributes are emitted (returns an empty object) — leaves the template
  // wrapper undetectable so CNAI ignores the whole form on surfaces where it should be off (e.g.
  // live-call widgets). Defaults to true.
  enable?: boolean;
  id: string;
  instructions?: string;
  name: string;
  type: CnaiTemplateType;
};

// builds the cnai-template-* attributes clinically.ai reads off the note wrapper; spread the result onto the target element
export function makeCnaiTemplateProps({
  asDataAttr,
  enable = true,
  id,
  instructions,
  name,
  type,
}: CnaiTemplateProps) {
  if (!enable) return {};

  return withCnaiAttrPrefix(
    {
      "cnai-template-id": id,
      "cnai-template-name": name,
      "cnai-template-type": type,
      ...(instructions !== undefined && { "cnai-template-instructions": instructions }),
    },
    asDataAttr,
  );
}

export type CnaiColumnFieldType =
  | "boolean"
  | "data"
  | "date"
  | "end_time"
  | "number"
  | "single_choice"
  | "start_end_time"
  | "start_time"
  | "string";

type CnaiTableCellProps = {
  // see makeCnaiProps — same data-cnai-* escape hatch
  asDataAttr?: boolean;
  col: number;
  // required for "single_choice" columns; only meaningful on a header cell (row 0)
  columnOptions?: string[];
  // required on header cells (row 0); omit on data cells (rows 1+)
  columnType?: CnaiColumnFieldType;
  row: number;
};

// builds the cnai-template-table-cell / cnai-template-column-field-* attributes for one cell of a
// "table"-type field; spread the result onto every header and data cell (see CNAI's Table fields doc)
export function makeCnaiTableCellProps({
  asDataAttr,
  col,
  columnOptions,
  columnType,
  row,
}: CnaiTableCellProps) {
  return withCnaiAttrPrefix(
    {
      "cnai-template-table-cell": `${row}:${col}`,
      ...(columnType !== undefined && { "cnai-template-column-field-type": columnType }),
      ...(columnOptions !== undefined && {
        "cnai-template-column-field-options": JSON.stringify(columnOptions),
      }),
    },
    asDataAttr,
  );
}

// builds the cnai-template-submit-button attribute; exists purely so every cnai-* attribute in the
// codebase goes through this file, no exceptions. CNAI checks the attribute's presence, not its
// value, but its attribute-prefix rules also treat an empty-string value as equivalent to the
// attribute being absent — so emit "true" rather than "" to satisfy both readings of the spec.
export function makeCnaiSubmitButtonProps({ asDataAttr }: { asDataAttr?: boolean } = {}) {
  return withCnaiAttrPrefix({ "cnai-template-submit-button": "true" }, asDataAttr);
}
